/**
 * Animated payoff chart for clip-v2 (plain SVG, exact pixels, deterministic).
 *
 * - "signed" curves are drawn twice, clipped above / below the $0 line, so the
 *   P&L line flips color exactly where it crosses breakeven.
 * - Domains are computed once per scene (computeChartDomain) so morphs and
 *   split-screen comparisons share stable axes.
 */
import React from "react";
import { VIDEO_FONT_FAMILY } from "../../RegisterVideoFonts";
import { formatMoney, pnlAtPrice } from "../scenarioExplainerTimeline";

const FONT = VIDEO_FONT_FAMILY;

function niceStep(range, targetTicks) {
  const raw = range / Math.max(1, targetTicks);
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * pow);
  return candidates.find((c) => c >= raw) || candidates[candidates.length - 1];
}

/**
 * X window around the prices that matter (clamped to the data), Y from the
 * curves inside that window, with headroom for labels.
 */
export function computeChartDomain(seriesList, focusPrices = [], { padPct = 0.045, minSpanPct = 0.12, extraPnls = [], capY = null } = {}) {
  const series = seriesList.filter(Boolean);
  const dataLo = Math.min(...series.map((s) => s[0].underlyingPrice));
  const dataHi = Math.max(...series.map((s) => s[s.length - 1].underlyingPrice));
  let minX = dataLo;
  let maxX = dataHi;
  const focus = focusPrices.filter((p) => Number.isFinite(p));
  if (focus.length) {
    const mid = (Math.min(...focus) + Math.max(...focus)) / 2;
    const pad = mid * padPct;
    const span = Math.max(Math.max(...focus) - Math.min(...focus) + pad * 2, mid * minSpanPct);
    minX = Math.max(dataLo, mid - span / 2);
    maxX = Math.min(dataHi, mid + span / 2);
  }
  const pnls = [0, ...extraPnls];
  series.forEach((s) => {
    s.forEach((p) => {
      if (p.underlyingPrice >= minX && p.underlyingPrice <= maxX) pnls.push(p.pnl);
    });
    pnls.push(pnlAtPrice(s, minX), pnlAtPrice(s, maxX));
  });
  const lo = Math.min(...pnls);
  let hi = Math.max(...pnls);
  // Optional upside cap (1.6x the largest max loss) so downside differences read clearly.
  if (capY?.length) hi = Math.min(hi, Math.max(...capY.map((v) => Math.abs(v))) * 1.6);
  const span = hi - lo || 1;
  return { minX, maxX, minY: lo - span * 0.16, maxY: hi + span * 0.14 };
}

/** Resample a series onto `n` evenly spaced prices across the domain (for morphing). */
export function resample(series, domain, n = 90) {
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const price = domain.minX + ((domain.maxX - domain.minX) * i) / (n - 1);
    out.push({ underlyingPrice: price, pnl: pnlAtPrice(series, price) });
  }
  return out;
}

export function morphSeries(a, b, t) {
  return a.map((p, i) => ({ underlyingPrice: p.underlyingPrice, pnl: p.pnl + (b[i].pnl - p.pnl) * t }));
}

export function chartGeometry(domain, width, height, fontSize, { showTicks = true } = {}) {
  const padL = fontSize * 0.7;
  const padR = fontSize * 0.7;
  const padT = fontSize * 2.5;
  const padB = showTicks ? fontSize * 2.1 : fontSize * 0.8;
  const plotW = Math.max(10, width - padL - padR);
  const plotH = Math.max(10, height - padT - padB);
  const x = (price) => padL + ((price - domain.minX) / (domain.maxX - domain.minX)) * plotW;
  const y = (pnl) => padT + (1 - (pnl - domain.minY) / (domain.maxY - domain.minY)) * plotH;
  return { padL, padR, padT, padB, plotW, plotH, x, y };
}

function pathOf(series, g, domain) {
  const pts = series.filter((p) => p.underlyingPrice >= domain.minX - 1e-9 && p.underlyingPrice <= domain.maxX + 1e-9);
  const withEdges = [
    { underlyingPrice: domain.minX, pnl: pnlAtPrice(series, domain.minX) },
    ...pts,
    { underlyingPrice: domain.maxX, pnl: pnlAtPrice(series, domain.maxX) },
  ];
  return withEdges.map((p, i) => `${i === 0 ? "M" : "L"}${g.x(p.underlyingPrice).toFixed(2)},${g.y(p.pnl).toFixed(2)}`).join(" ");
}

function Pill({ x, y, text, fill, color, fontSize, minX, maxX }) {
  const padX = fontSize * 0.6;
  const w = text.length * fontSize * 0.6 + padX * 2;
  const h = fontSize * 1.6;
  const left = Math.min(Math.max(x - w / 2, minX), maxX - w);
  return (
    <g>
      <rect x={left} y={y - h / 2} width={w} height={h} rx={h / 2} fill={fill} />
      <text x={left + w / 2} y={y + fontSize * 0.36} textAnchor="middle" fontSize={fontSize} fontWeight={800} fill={color} fontFamily={FONT}>
        {text}
      </text>
    </g>
  );
}

/**
 * curves: [{ points, style: "signed" | "ghost", reveal (0-1), opacity }]
 * vLines: [{ price, label, color, opacity, labelY? ("top" | "bottom") }]
 * hLines: [{ pnl, label, color, opacity }]
 * marker: { price, pnl, opacity, pill }   (pnl = where the dot sits)
 */
export function ClipChart({
  idPrefix,
  width,
  height,
  domain,
  colors,
  fontSize,
  curves = [],
  vLines = [],
  hLines = [],
  marker = null,
  showTicks = true,
  background = true,
  fillOpacity = 0.16,
}) {
  const g = chartGeometry(domain, width, height, fontSize, { showTicks });
  const zeroY = Math.max(g.padT, Math.min(g.padT + g.plotH, g.y(0)));
  const step = niceStep(domain.maxX - domain.minX, 4);
  const ticks = [];
  for (let v = Math.ceil(domain.minX / step) * step; v <= domain.maxX + 1e-9; v += step) ticks.push(v);
  const strokeW = fontSize * 0.3;
  const ids = { above: `${idPrefix}-above`, below: `${idPrefix}-below`, plot: `${idPrefix}-plot` };

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block", overflow: "visible" }}>
      <defs>
        <clipPath id={ids.above}>
          <rect x={0} y={-height} width={width} height={zeroY + height} />
        </clipPath>
        <clipPath id={ids.below}>
          <rect x={0} y={zeroY} width={width} height={height * 2} />
        </clipPath>
        <clipPath id={ids.plot}>
          <rect x={g.padL} y={g.padT - fontSize} width={g.plotW} height={g.plotH + fontSize * 2} />
        </clipPath>
        {curves.map((c, i) => (
          <clipPath key={`rev-${i}`} id={`${idPrefix}-rev-${i}`}>
            <rect x={g.padL - strokeW} y={-height} width={(g.plotW + strokeW * 2) * Math.max(0, Math.min(1, c.reveal ?? 1))} height={height * 3} />
          </clipPath>
        ))}
      </defs>

      {background ? <rect x={0} y={0} width={width} height={height} rx={fontSize * 1.1} fill={colors.surface} /> : null}

      {showTicks
        ? ticks.map((v) => (
            <g key={`tick-${v}`}>
              <line x1={g.x(v)} x2={g.x(v)} y1={g.padT} y2={g.padT + g.plotH} stroke={colors.muted} strokeOpacity={0.12} strokeWidth={2} />
              <text
                x={g.x(v)}
                y={g.padT + g.plotH + fontSize * 1.45}
                textAnchor={g.x(v) > g.padL + g.plotW - fontSize * 2 ? "end" : g.x(v) < g.padL + fontSize * 2 ? "start" : "middle"}
                fontSize={fontSize * 0.9}
                fontWeight={700}
                fill={colors.muted}
                fontFamily={FONT}
              >
                {formatMoney(v)}
              </text>
            </g>
          ))
        : null}

      <line x1={g.padL} x2={g.padL + g.plotW} y1={zeroY} y2={zeroY} stroke={colors.muted} strokeOpacity={0.6} strokeWidth={3} />
      <text x={g.padL + fontSize * 0.3} y={zeroY - fontSize * 0.45} fontSize={fontSize * 0.85} fontWeight={800} fill={colors.muted} fontFamily={FONT}>
        $0
      </text>

      {hLines.map((h, i) => (
        <g key={`h-${i}`} opacity={h.opacity ?? 1}>
          <line x1={g.padL} x2={g.padL + g.plotW} y1={g.y(h.pnl)} y2={g.y(h.pnl)} stroke={h.color} strokeWidth={4} strokeDasharray="14 10" />
          {h.label ? (
            <text
              x={g.padL + g.plotW - fontSize * 0.3}
              y={g.y(h.pnl) + fontSize * 1.35}
              textAnchor="end"
              fontSize={fontSize}
              fontWeight={900}
              fill={h.color}
              fontFamily={FONT}
            >
              {h.label}
            </text>
          ) : null}
        </g>
      ))}

      <g clipPath={`url(#${ids.plot})`}>
        {curves.map((c, i) => {
          const d = pathOf(c.points, g, domain);
          const area = `${d} L${(g.padL + g.plotW).toFixed(2)},${zeroY.toFixed(2)} L${g.padL.toFixed(2)},${zeroY.toFixed(2)} Z`;
          const rev = `url(#${idPrefix}-rev-${i})`;
          if (c.style === "ghost") {
            return (
              <g key={`c-${i}`} clipPath={rev} opacity={c.opacity ?? 1}>
                <path
                  d={d}
                  fill="none"
                  stroke={colors.muted}
                  strokeWidth={strokeW * 0.55}
                  strokeDasharray={`${fontSize * 0.55} ${fontSize * 0.45}`}
                  strokeLinejoin="round"
                />
              </g>
            );
          }
          return (
            <g key={`c-${i}`} clipPath={rev} opacity={c.opacity ?? 1}>
              <g clipPath={`url(#${ids.above})`}>
                <path d={area} fill={colors.accent} fillOpacity={fillOpacity} />
                <path d={d} fill="none" stroke={colors.accent} strokeWidth={strokeW} strokeLinejoin="round" strokeLinecap="round" />
              </g>
              <g clipPath={`url(#${ids.below})`}>
                <path d={area} fill={colors.negative} fillOpacity={fillOpacity} />
                <path d={d} fill="none" stroke={colors.negative} strokeWidth={strokeW} strokeLinejoin="round" strokeLinecap="round" />
              </g>
            </g>
          );
        })}
      </g>

      {vLines.map((v, i) => {
        if (v.price < domain.minX || v.price > domain.maxX) return null;
        const vx = g.x(v.price);
        return (
          <g key={`v-${i}`} opacity={v.opacity ?? 1}>
            <line x1={vx} x2={vx} y1={g.padT} y2={g.padT + g.plotH} stroke={v.color} strokeWidth={4} strokeDasharray="10 9" />
            {v.label ? (
              <Pill
                x={vx}
                y={v.labelY === "bottom" ? g.padT + g.plotH - fontSize * 1.1 : g.padT + fontSize * 1.1}
                text={v.label}
                fill={colors.background}
                color={v.color}
                fontSize={fontSize * 0.88}
                minX={g.padL}
                maxX={g.padL + g.plotW}
              />
            ) : null}
          </g>
        );
      })}

      {marker && marker.price >= domain.minX && marker.price <= domain.maxX ? (
        <g opacity={marker.opacity ?? 1}>
          <line x1={g.x(marker.price)} x2={g.x(marker.price)} y1={g.padT - fontSize * 0.5} y2={g.padT + g.plotH} stroke={colors.text} strokeWidth={3} />
          {marker.dot === false ? null : (
            <circle
              cx={g.x(marker.price)}
              cy={g.y(marker.pnl)}
              r={fontSize * 0.62 * (marker.pulse ?? 1)}
              fill={marker.pnl < 0 ? colors.negative : colors.accent}
              stroke={colors.background}
              strokeWidth={fontSize * 0.2}
            />
          )}
          {marker.pill ? (
            <Pill
              x={g.x(marker.price)}
              y={g.padT - fontSize * 1.35}
              text={marker.pill}
              fill={colors.text}
              color={colors.background}
              fontSize={fontSize * 0.95}
              minX={0}
              maxX={width}
            />
          ) : null}
        </g>
      ) : null}
    </svg>
  );
}
